import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import {
  ClipboardCheck,
  Check,
  X,
  Minus,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Save,
  CheckCheck,
  Share2,
  Trash2,
  Mail,
  MessageSquare,
  Printer,
  Unlock,
  Truck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { StatusBadge } from '../components/StatusBadge';
import LocalVehicleSelector from '../components/LocalVehicleSelector';
import { InspectionVehicleSelector } from '../components/InspectionVehicleSelector';
import { getTodayDate } from '../lib/utils';
import { toast } from 'sonner';

// Liste des défectuosités conforme à la SAAQ (Société de l'assurance automobile du Québec)
// Règlement sur les normes de sécurité des véhicules routiers
// applies_to: ['truck', 'trailer'] indique a quel(s) type(s) de vehicule la categorie s'applique
const inspectionCategories = [
  {
    id: 'attelage',
    name: '1. Attelage',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'attelage_fixation', name: 'Éléments de fixation du dispositif d\'attelage' },
      { id: 'attelage_surete', name: 'Attache de sûreté ou raccord' },
      { id: 'attelage_plaque', name: 'Plaque d\'attelage ou pivot d\'attelage' },
      { id: 'attelage_sellette', name: 'Sellette (mouvement avec le cadre)' },
      { id: 'attelage_mecanisme', name: 'Mécanisme d\'attelage (fermeture/verrouillage)' },
      { id: 'attelage_goupilles', name: 'Goupilles de blocage' }
    ]
  },
  {
    id: 'chassis',
    name: '2. Châssis et carrosserie',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'chassis_longeron', name: 'Longeron (fissures, risque de casser)' },
      { id: 'chassis_traverse', name: 'Traverse (fissures, cassée)' },
      { id: 'chassis_carrosserie', name: 'Éléments fixes de la carrosserie' },
      { id: 'chassis_goupilles', name: 'Goupilles de blocage train roulant coulissant' }
    ]
  },
  {
    id: 'chauffage',
    name: '3. Chauffage et dégivrage',
    applies_to: ['truck'],
    items: [
      { id: 'chauffage_soufflerie', name: 'Soufflerie du pare-brise' },
      { id: 'chauffage_degivrage', name: 'Système de dégivrage' }
    ]
  },
  {
    id: 'commandes',
    name: '4. Commandes du conducteur',
    applies_to: ['truck'],
    items: [
      { id: 'commandes_accelerateur', name: 'Accélérateur (retour au ralenti)' },
      { id: 'commandes_embrayage', name: 'Embrayage' },
      { id: 'commandes_klaxon', name: 'Klaxon' }
    ]
  },
  {
    id: 'direction',
    name: '5. Direction',
    applies_to: ['truck'],
    items: [
      { id: 'direction_colonne', name: 'Colonne de direction' },
      { id: 'direction_volant', name: 'Volant (ajustable, position)' },
      { id: 'direction_servo_niveau', name: 'Niveau liquide servodirection' },
      { id: 'direction_servo_courroie', name: 'Courroie pompe servodirection' },
      { id: 'direction_servo_fonct', name: 'Fonctionnement servodirection' }
    ]
  },
  {
    id: 'essuieglaces',
    name: '6. Essuie-glaces et lave-glace',
    applies_to: ['truck'],
    items: [
      { id: 'essuie_conducteur', name: 'Essuie-glace côté conducteur' },
      { id: 'essuie_passager', name: 'Essuie-glace côté passager' },
      { id: 'essuie_laveglace', name: 'Système de lave-glace' }
    ]
  },
  {
    id: 'urgence',
    name: '7. Matériel d\'urgence',
    applies_to: ['truck'],
    items: [
      { id: 'urgence_trousse', name: 'Trousse de premiers soins' },
      { id: 'urgence_extincteur', name: 'Extincteur chimique' },
      { id: 'urgence_triangles', name: 'Triangles de signalisation' }
    ]
  },
  {
    id: 'phares',
    name: '8. Phares et feux',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'phares_croisement', name: 'Phares de croisement' },
      { id: 'phares_position', name: 'Feux de position' },
      { id: 'phares_clignotants', name: 'Feux de changement de direction' },
      { id: 'phares_freinage', name: 'Feux de freinage' },
      { id: 'phares_plaque', name: 'Feu de la plaque d\'immatriculation' },
      { id: 'phares_recul', name: 'Feux de recul' }
    ]
  },
  {
    id: 'pneus',
    name: '9. Pneus',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'pneus_usure', name: 'Indicateurs d\'usure / profondeur rainures' },
      { id: 'pneus_dommages', name: 'Dommages (toile, ceinture acier visible)' },
      { id: 'pneus_deformation', name: 'Déformation / séparation bande roulement' },
      { id: 'pneus_valve', name: 'Valves (usure, dommages)' },
      { id: 'pneus_pression', name: 'Pression / renflement / fuite d\'air' },
      { id: 'pneus_contact', name: 'Contact avec partie fixe du véhicule' }
    ]
  },
  {
    id: 'portieres',
    name: '10. Portières et autres issues',
    applies_to: ['truck'],
    items: [
      { id: 'portieres_conducteur', name: 'Portière du conducteur (ouverture)' },
      { id: 'portieres_fermeture', name: 'Fermeture sécuritaire des portières' },
      { id: 'portieres_issues', name: 'Autres issues de secours' }
    ]
  },
  {
    id: 'retroviseurs',
    name: '11. Rétroviseurs et vitrage',
    applies_to: ['truck'],
    items: [
      { id: 'retro_parebrise', name: 'Pare-brise (visibilité)' },
      { id: 'retro_vitres', name: 'Vitres latérales (visibilité)' },
      { id: 'retro_gauche', name: 'Rétroviseur extérieur gauche' },
      { id: 'retro_droit', name: 'Rétroviseur extérieur droit' },
      { id: 'retro_fixation', name: 'Fixation des rétroviseurs' }
    ]
  },
  {
    id: 'roues',
    name: '12. Roues, moyeux et pièces de fixation',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'roues_lubrifiant', name: 'Lubrifiant roulement de roue' },
      { id: 'roues_fixation', name: 'Pièces de fixation (écrous, goujons)' },
      { id: 'roues_dommages', name: 'Roues (dommages, soudures)' },
      { id: 'roues_secours', name: 'Roue de secours (fixation)' }
    ]
  },
  {
    id: 'siege',
    name: '13. Siège',
    applies_to: ['truck'],
    items: [
      { id: 'siege_conducteur', name: 'Siège du conducteur (position)' },
      { id: 'siege_ceinture', name: 'Ceinture de sécurité conducteur' }
    ]
  },
  {
    id: 'suspension',
    name: '14. Suspension',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'susp_ressorts', name: 'Ressorts (lames, hélicoïdaux)' },
      { id: 'susp_lame_maitresse', name: 'Lame maîtresse' },
      { id: 'susp_pneumatique', name: 'Suspension pneumatique (ballons, fuites)' },
      { id: 'susp_fixation', name: 'Fixation de l\'essieu' },
      { id: 'susp_composite', name: 'Lame en composite (fissures)' },
      { id: 'susp_essieu', name: 'Essieu (parallélisme, position)' }
    ]
  },
  {
    id: 'carburant',
    name: '15. Système d\'alimentation en carburant',
    applies_to: ['truck'],
    items: [
      { id: 'carburant_reservoir', name: 'Réservoir (fixation)' },
      { id: 'carburant_bouchon', name: 'Bouchon du réservoir' },
      { id: 'carburant_fuites', name: 'Fuites de carburant' }
    ]
  },
  {
    id: 'echappement',
    name: '16. Système d\'échappement',
    applies_to: ['truck'],
    items: [
      { id: 'echap_fuites', name: 'Fuites de gaz d\'échappement' },
      { id: 'echap_habitacle', name: 'Infiltration dans l\'habitacle' },
      { id: 'echap_fixation', name: 'Fixation du système' }
    ]
  },
  {
    id: 'freins_electriques',
    name: '17. Système de freins électriques',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'frein_elec_raccord', name: 'Raccords et câbles électriques' },
      { id: 'frein_elec_capacite', name: 'Capacité de freinage' }
    ]
  },
  {
    id: 'freins_hydrauliques',
    name: '18. Système de freins hydrauliques',
    applies_to: ['truck'],
    items: [
      { id: 'frein_hydr_liquide', name: 'Niveau liquide maître-cylindre' },
      { id: 'frein_hydr_pedale', name: 'Course de la pédale de frein' },
      { id: 'frein_hydr_temoin', name: 'Témoin lumineux freins' },
      { id: 'frein_hydr_stationnement', name: 'Frein de stationnement' },
      { id: 'frein_hydr_servo', name: 'Freins assistés / servofrein' },
      { id: 'frein_hydr_capacite', name: 'Capacité de freinage' }
    ]
  },
  {
    id: 'freins_pneumatiques',
    name: '19. Système de freins pneumatiques',
    applies_to: ['truck', 'trailer'],
    items: [
      { id: 'frein_pneu_avertisseur', name: 'Avertisseur sonore basse pression' },
      { id: 'frein_pneu_temoin', name: 'Avertisseurs lumineux basse pression' },
      { id: 'frein_pneu_regulateur', name: 'Régulateur de pression' },
      { id: 'frein_pneu_fuites', name: 'Fuites d\'air' },
      { id: 'frein_pneu_compresseur', name: 'Compresseur d\'air' },
      { id: 'frein_pneu_stationnement', name: 'Frein de stationnement / urgence' },
      { id: 'frein_pneu_capacite', name: 'Capacité de freinage' }
    ]
  }
];

// Helper: retourne les categories visibles selon le type de vehicule selectionne
const getVisibleCategories = (vehicleType) => {
  if (!vehicleType) return inspectionCategories;
  return inspectionCategories.filter(
    (c) => !c.applies_to || c.applies_to.includes(vehicleType)
  );
};

export const InspectionPage = () => {
  const { get, post, put } = useApi();
  const { user } = useAuth();
  const [expandedCategories, setExpandedCategories] = useState(['attelage']);
  const [inspectionItems, setInspectionItems] = useState({});
  const [notes, setNotes] = useState('');
  const [mileage, setMileage] = useState('');
  const [existingInspection, setExistingInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Selected vehicle (from LocalVehicleSelector / localStorage) or fallback to /driver/vehicle
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [driverVehicle, setDriverVehicle] = useState(null); // legacy primary vehicle

  // Wizard mode: enchaine plusieurs inspections puis signe une fois
  // shape: { queue: [v1,v2,v3], currentIdx: 0, completedItems: { v1.id: itemsObj, v2.id: itemsObj }, mileages: { v1.id: km } }
  const [wizardMode, setWizardMode] = useState(null);

  // Filter categories by vehicle type (truck or trailer). If no type set, show all.
  const visibleCategories = getVisibleCategories(selectedVehicle?._vehicle_type);

  // Signature state
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePhone, setSharePhone] = useState('');

  // Whether we are editing (re-opened) an existing completed inspection
  const isReopened = !!(existingInspection && existingInspection.reopened_at);
  // The form is locked (read-only) when an inspection is signed and NOT re-opened
  const isLocked = !!(existingInspection && existingInspection.signature && !isReopened);

  useEffect(() => {
    initVehicleSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const location = useLocation();

  const initVehicleSelection = async () => {
    // Priorité 1 : navigation depuis le banner avec state.vehicle
    const stateVehicle = location?.state?.vehicle;
    if (stateVehicle?.id) {
      setSelectedVehicle({
        id: stateVehicle.id,
        unit_number: stateVehicle.unit_number,
        plate_number: stateVehicle.plate_number,
        brand: stateVehicle.make,
        model: stateVehicle.model,
        _from_assignment: true,
        _vehicle_type: stateVehicle.vehicle_type,
      });
      return;
    }

    // Priorité 2 : URL ?vehicle_id=
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get('vehicle_id');
    if (focusId) {
      try {
        const r = await get('/driver/assignment/current');
        if (r.success && r.data) {
          const all = [r.data.truck, ...(r.data.trailers || [])].filter(Boolean);
          const v = all.find((x) => x.id === focusId);
          if (v) {
            setSelectedVehicle({
              id: v.id,
              unit_number: v.unit_number,
              plate_number: v.plate_number,
              brand: v.make,
              model: v.model,
              _from_assignment: true,
              _vehicle_type: v.vehicle_type,
            });
            return;
          }
        }
      } catch (e) { /* silent */ }
    }

    // Sinon : on laisse selectedVehicle a null pour afficher le selecteur
    // (le selecteur fetchera lui-meme l'assignment)
  };

  // Reload existing inspection whenever the selected vehicle changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const vid = selectedVehicle?.id || (driverVehicle?.unit_number ? 'primary' : null);
    if (vid) {
      setLoading(true);
      checkExistingInspection(selectedVehicle);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle]);

  const fetchDriverVehicle = async () => {
    try {
      const result = await get('/driver/vehicle');
      if (result.success && result.data && result.data.unit_number) {
        setDriverVehicle(result.data);
        // Auto-select the primary vehicle if no local vehicle yet selected
        if (!selectedVehicle) {
          setSelectedVehicle({
            id: 'primary',
            unit_number: result.data.unit_number,
            plate_number: result.data.plate_number,
            trailer_number: result.data.trailer_number,
            trailer_plate: result.data.trailer_plate,
            _is_primary: true,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching vehicle:', err);
    }
  };

  const checkExistingInspection = useCallback(async (vehicle) => {
    try {
      const today = getTodayDate();
      const result = await get('/inspections', { date: today });
      const list = (result.success && result.data) || [];
      // Filter by the currently selected vehicle (match on unit_number)
      const vehicleUnit = vehicle?.unit_number;
      const matching = list.find((i) => {
        if (!vehicleUnit) return true;
        return (i.vehicle_unit || '').toLowerCase() === String(vehicleUnit).toLowerCase();
      });
      if (matching) {
        setExistingInspection(matching);
        setMileage(matching.mileage?.toString() || '');
        setNotes(matching.notes || '');
        setSignature(matching.signature || null);
        const items = {};
        matching.items?.forEach((item) => {
          items[item.item] = { status: item.status, notes: item.notes };
        });
        setInspectionItems(items);
      } else {
        // Reset state for a new (per-vehicle) inspection
        setExistingInspection(null);
        setMileage('');
        setNotes('');
        setSignature(null);
        setInspectionItems({});
      }
    } catch (err) {
      console.error('Error checking inspection:', err);
    } finally {
      setLoading(false);
    }
  }, [get]);

  // Re-open a completed inspection (same day only)
  const handleReopen = async () => {
    if (!existingInspection?.id) return;
    try {
      const res = await post(`/inspections/${existingInspection.id}/reopen`, {});
      if (res.success) {
        const updated = res.data || res.response?.data;
        setExistingInspection(updated);
        setSignature(null);
        toast.success('Inspection re-ouverte — ajoutez vos corrections et re-signez');
      } else {
        toast.error(res.error || 'Impossible de re-ouvrir');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la re-ouverture');
    }
  };

  // Mark all items as OK in one click
  const markAllConform = () => {
    const allItems = {};
    visibleCategories.forEach(category => {
      category.items.forEach(item => {
        allItems[item.id] = { status: 'ok', notes: null };
      });
    });
    setInspectionItems(allItems);
    // Expand all categories to show the result
    setExpandedCategories(visibleCategories.map(c => c.id));
    toast.success('Tous les éléments marqués conformes');
  };

  // Signature canvas functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (existingInspection) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || existingInspection) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    if (e) e.preventDefault();
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      setSignature(dataUrl);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature(null);
    }
  };

  useEffect(() => {
    if (!loading && (!existingInspection || isReopened)) {
      initCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, existingInspection, isReopened]);

  // Generate inspection report text for sharing
  const generateInspectionReport = () => {
    const inspection = existingInspection;
    if (!inspection) return '';
    
    let report = `═══════════════════════════════════════\n`;
    report += `   RAPPORT D'INSPECTION PRÉ-DÉPART\n`;
    report += `   Conforme au Règlement SAAQ\n`;
    report += `═══════════════════════════════════════\n\n`;
    
    report += `📅 Date: ${inspection.date}\n`;
    report += `🚛 Kilométrage: ${inspection.mileage} km\n`;
    report += `📋 Statut: ${inspection.overall_status === 'passed' ? '✅ CONFORME' : '❌ NON-CONFORME'}\n\n`;
    
    report += `═══════════════════════════════════════\n`;
    report += `   DÉTAIL DE L'INSPECTION (19 catégories)\n`;
    report += `═══════════════════════════════════════\n\n`;
    
    inspectionCategories.forEach(category => {
      report += `▸ ${category.name}\n`;
      report += `─────────────────────────────────────\n`;
      
      category.items.forEach(item => {
        const itemData = inspectionItems[item.id];
        if (!itemData) return; // skip items hors scope vehicule
        const statusIcon = itemData?.status === 'ok' ? '✅' : 
                          itemData?.status === 'defect' ? '❌' : '➖';
        const statusText = itemData?.status === 'ok' ? 'OK' : 
                          itemData?.status === 'defect' ? 'DÉFAUT' : 'N/A';
        report += `  ${statusIcon} ${item.name}: ${statusText}\n`;
      });
      report += `\n`;
    });
    
    if (notes) {
      report += `═══════════════════════════════════════\n`;
      report += `   REMARQUES\n`;
      report += `═══════════════════════════════════════\n`;
      report += `${notes}\n\n`;
    }
    
    report += `═══════════════════════════════════════\n`;
    report += `   SIGNATURE DU CHAUFFEUR\n`;
    report += `═══════════════════════════════════════\n`;
    report += signature ? `✍️ Signé électroniquement\n` : `❌ Non signé\n`;
    report += `\nRapport conforme au Règlement sur les normes\n`;
    report += `de sécurité des véhicules routiers (SAAQ)\n`;
    report += `Généré le ${new Date().toLocaleString('fr-CA')}\n`;
    
    return report;
  };

  // Share functions
  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Rapport d'inspection - ${existingInspection?.date}`);
    const body = encodeURIComponent(generateInspectionReport());
    window.location.href = `mailto:${shareEmail}?subject=${subject}&body=${body}`;
    setShowShareDialog(false);
  };

  const handleShareSMS = () => {
    const body = encodeURIComponent(generateInspectionReport());
    window.location.href = `sms:${sharePhone}?body=${body}`;
    setShowShareDialog(false);
  };

  const handlePrint = () => {
    setShowShareDialog(false);
    setTimeout(() => window.print(), 300);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const setItemStatus = (itemId, status) => {
    setInspectionItems(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], status }
    }));
  };

  const getItemStatus = (itemId) => {
    return inspectionItems[itemId]?.status || null;
  };

  const getCategoryStatus = (category) => {
    const statuses = category.items.map(item => getItemStatus(item.id));
    if (statuses.every(s => s === 'ok')) return 'ok';
    if (statuses.some(s => s === 'defect')) return 'defect';
    if (statuses.every(s => s !== null)) return 'ok';
    return null;
  };

  const isComplete = () => {
    const allItems = visibleCategories.flatMap(cat => cat.items);
    return allItems.every(item => getItemStatus(item.id) !== null) && 
           mileage.trim() !== '' && 
           signature !== null;
  };

  const hasDefects = () => {
    return Object.values(inspectionItems).some(item => item.status === 'defect');
  };

  const handleSubmit = async () => {
    // Debug: log what's missing
    const allItems = visibleCategories.flatMap(cat => cat.items);
    const missingItems = allItems.filter(item => getItemStatus(item.id) === null);
    
    if (missingItems.length > 0) {
      toast.error(`Veuillez compléter: ${missingItems.map(i => i.name).join(', ')}`);
      return;
    }
    
    if (!mileage.trim()) {
      toast.error('Veuillez entrer le kilométrage');
      return;
    }
    
    if (!signature) {
      toast.error('Veuillez signer l\'inspection');
      return;
    }

    setSubmitting(true);

    const items = [];
    visibleCategories.forEach(category => {
      category.items.forEach(item => {
        const itemData = inspectionItems[item.id];
        items.push({
          category: category.id,
          item: item.id,
          status: itemData?.status || 'na',
          notes: itemData?.notes || null
        });
      });
    });

    const vehicleUnit = selectedVehicle?.unit_number || driverVehicle?.unit_number || '';
    const inspectionData = {
      vehicle_id: selectedVehicle?.id || 'primary',
      vehicle_unit: vehicleUnit,
      vehicle_plate: selectedVehicle?.plate_number || driverVehicle?.plate_number || '',
      vehicle_name: selectedVehicle ? [selectedVehicle.brand, selectedVehicle.model].filter(Boolean).join(' ') : '',
      type: 'pre_departure',
      date: getTodayDate(),
      mileage: parseInt(mileage),
      items,
      overall_status: hasDefects() ? 'failed' : 'passed',
      notes: notes || null,
      signature: signature
    };

    try {
      // If an existing inspection was re-opened, PUT (update). Otherwise POST (create).
      const isUpdate = existingInspection && existingInspection.id;
      const result = isUpdate
        ? await put(`/inspections/${existingInspection.id}`, inspectionData)
        : await post('/inspections', inspectionData);
      if (result.success) {
        toast.success(isUpdate ? 'Inspection mise a jour' : 'Inspection enregistree avec succes!');
        const saved = result.data || result.response?.data;
        // Preserve vehicle label for future filtering
        if (saved && vehicleUnit && !saved.vehicle_unit) saved.vehicle_unit = vehicleUnit;
        setExistingInspection(saved);
      } else {
        toast.error(result.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper p-4" data-testid="inspection-loading">
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-800 rounded-xl p-4 animate-pulse">
              <div className="h-6 bg-slate-700 rounded w-1/3 mb-4" />
              <div className="space-y-2">
                <div className="h-12 bg-slate-700 rounded" />
                <div className="h-12 bg-slate-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Si aucun vehicule selectionne ET pas de mode wizard -> afficher le selecteur
  if (!selectedVehicle?._vehicle_type && !wizardMode) {
    return (
      <div className="page-wrapper" data-testid="inspection-page">
        <div className="app-header">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Inspection</h1>
              <p className="text-slate-400 text-sm">Choisissez un véhicule</p>
            </div>
          </div>
        </div>
        <InspectionVehicleSelector
          onSelect={(v) => {
            setSelectedVehicle({
              id: v.id,
              unit_number: v.unit_number,
              plate_number: v.plate_number,
              brand: v.make,
              model: v.model,
              _from_assignment: true,
              _vehicle_type: v._vehicle_type,
            });
          }}
          onStartWizard={(vehiclesQueue) => {
            // Demarrer le mode wizard : enchaine les inspections
            setWizardMode({
              queue: vehiclesQueue,
              currentIdx: 0,
              completedItems: {},
              mileages: {},
            });
            const first = vehiclesQueue[0];
            setSelectedVehicle({
              id: first.id,
              unit_number: first.unit_number,
              plate_number: first.plate_number,
              brand: first.make,
              model: first.model,
              _from_assignment: true,
              _vehicle_type: first._vehicle_type,
            });
            // Reset items pour la 1ere inspection
            setInspectionItems({});
            setMileage('');
            setSignature(null);
            toast.info(`Wizard demarre — ${vehiclesQueue.length} vehicules a inspecter`);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {/* Main UI - Hidden during print */}
      <div className="page-wrapper print:hidden" data-testid="inspection-page">
        {/* Header */}
        <div className="app-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Inspection</h1>
              <p className="text-slate-400 text-sm">
                {wizardMode
                  ? `Mode rapide · Véhicule ${wizardMode.currentIdx + 1}/${wizardMode.queue.length}`
                  : 'Optionnel — Backup ELD'}
              </p>
            </div>
          </div>
          {!wizardMode && (
            <button
              onClick={() => setSelectedVehicle(null)}
              className="text-xs text-slate-400 hover:text-amber-400 px-2 py-1"
              data-testid="back-to-selector-btn"
            >
              ← Changer
            </button>
          )}
          {existingInspection && (
            <StatusBadge status={existingInspection.overall_status} />
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Vehicle Selector - permet de basculer entre plusieurs vehicules dans la journee */}
        {/* Vehicle Selector legacy (cache si vehicule vient de l'assignment) */}
        {!selectedVehicle?._from_assignment && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h2 className="text-sm font-bold text-amber-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Truck size={14} />
            Vehicule inspecte
          </h2>
          <LocalVehicleSelector
            driverId={user?.id}
            selected={selectedVehicle}
            onChange={(v) => {
              // Preserver le _vehicle_type s'il existe deja (carte cliquee depuis le selecteur)
              if (v && selectedVehicle?._vehicle_type) {
                setSelectedVehicle({ ...v, _vehicle_type: selectedVehicle._vehicle_type });
              } else {
                setSelectedVehicle(v);
              }
            }}
            disabled={!!(existingInspection && existingInspection.signature && !isReopened)}
          />
          <p className="text-[11px] text-slate-500 mt-2">
            Changez de vehicule pour demarrer une inspection separee. Vos vehicules sont enregistres localement sur ce telephone.
          </p>
        </div>
        )}

        {/* Vehicle info read-only quand vient de l'assignment */}
        {selectedVehicle?._from_assignment && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <h2 className="text-sm font-bold text-amber-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Truck size={14} />
              Vehicule a inspecter
            </h2>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg font-bold text-white">
                    {selectedVehicle.unit_number ? `#${selectedVehicle.unit_number}` : selectedVehicle.plate_number}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    selectedVehicle._vehicle_type === 'trailer'
                      ? 'bg-blue-500/20 text-blue-300'
                      : 'bg-amber-500/20 text-amber-300'
                  }`}>
                    {selectedVehicle._vehicle_type === 'trailer' ? 'REMORQUE' : 'CAMION'}
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  {[selectedVehicle.brand, selectedVehicle.model].filter(Boolean).join(' ') || selectedVehicle.plate_number}
                </p>
              </div>
              <button
                onClick={() => setSelectedVehicle(null)}
                className="text-xs text-amber-400 hover:text-amber-300 underline"
                data-testid="change-vehicle-link"
              >
                Changer
              </button>
            </div>
          </div>
        )}

        {/* Reopened banner */}
        {isReopened && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-2">
            <Unlock size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-300">Inspection re-ouverte</p>
              <p className="text-xs text-slate-300">Ajoutez vos corrections et re-signez en bas pour confirmer.</p>
            </div>
          </div>
        )}

        {/* Driver & Vehicle Info Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h2 className="text-sm font-bold text-amber-500 uppercase tracking-wider mb-3">
            Identification
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-xs uppercase">Chauffeur</p>
              <p className="text-white font-medium">{user?.first_name} {user?.last_name}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase">Permis</p>
              <p className="text-white font-medium">{user?.license_number || '—'}</p>
            </div>
            {selectedVehicle ? (
              <>
                <div>
                  <p className="text-slate-500 text-xs uppercase">Tracteur (Unite)</p>
                  <p className="text-white font-medium">#{selectedVehicle.unit_number}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase">Plaque tracteur</p>
                  <p className="text-white font-medium">{selectedVehicle.plate_number || '—'}</p>
                </div>
                {selectedVehicle.trailer_number && (
                  <>
                    <div>
                      <p className="text-slate-500 text-xs uppercase">Remorque</p>
                      <p className="text-white font-medium">#{selectedVehicle.trailer_number}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs uppercase">Plaque remorque</p>
                      <p className="text-white font-medium">{selectedVehicle.trailer_plate || '—'}</p>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="col-span-2 text-amber-400 text-sm">
                Selectionnez un vehicule ci-dessus pour demarrer l'inspection
              </div>
            )}
          </div>
        </div>

        {/* Mileage Input */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <label className="label-uppercase">Kilometrage actuel</label>
          <input
            type="number"
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            placeholder="Ex: 125430"
            className="input-large mt-2"
            disabled={!!(existingInspection && existingInspection.signature && !isReopened)}
            data-testid="mileage-input"
          />
        </div>

        {/* Bandeau type d'inspection */}
        {selectedVehicle && selectedVehicle._vehicle_type && (
          <div
            className={`rounded-xl p-3 border-l-4 ${
              selectedVehicle._vehicle_type === 'trailer'
                ? 'bg-blue-500/10 border-blue-500'
                : 'bg-amber-500/10 border-amber-500'
            }`}
            data-testid="inspection-scope-banner"
          >
            <p className="text-white font-bold text-sm">
              {selectedVehicle._vehicle_type === 'trailer' ? '🛞 Inspection REMORQUE' : '🚛 Inspection CAMION'}
            </p>
            <p className="text-xs text-slate-300 mt-1">
              {visibleCategories.length} catégorie(s) applicable(s) — les autres sections sont masquées.
            </p>
          </div>
        )}

        {/* Reopen button - only if inspection is completed (signed) for the selected vehicle */}
        {existingInspection && existingInspection.signature && !isReopened && (
          <Button
            onClick={handleReopen}
            className="w-full h-14 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-base font-bold"
            data-testid="reopen-inspection-btn"
          >
            <Unlock size={20} className="mr-2" />
            Re-ouvrir cette inspection (ajouter un bris)
          </Button>
        )}

        {/* TOUT CONFORME Button */}
        {!isLocked && (
          <Button
            onClick={markAllConform}
            className="w-full h-16 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-lg font-bold"
            data-testid="mark-all-conform-btn"
          >
            <CheckCheck size={28} className="mr-3" />
            TOUT CONFORME
          </Button>
        )}

        {/* Inspection Categories */}
        {visibleCategories.map(category => {
          const isExpanded = expandedCategories.includes(category.id);
          const categoryStatus = getCategoryStatus(category);
          
          return (
            <div 
              key={category.id}
              className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
              data-testid={`category-${category.id}`}
            >
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-white">{category.name}</h3>
                  {categoryStatus && <StatusBadge status={categoryStatus} />}
                </div>
                {isExpanded ? (
                  <ChevronUp className="text-slate-400" size={24} />
                ) : (
                  <ChevronDown className="text-slate-400" size={24} />
                )}
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 space-y-2">
                  {category.items.map(item => {
                    const status = getItemStatus(item.id);
                    
                    return (
                      <div 
                        key={item.id}
                        className="inspection-item"
                        data-testid={`item-${item.id}`}
                      >
                        <span className="text-white font-medium">{item.name}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => !isLocked && setItemStatus(item.id, 'ok')}
                            className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              status === 'ok'
                                ? 'bg-green-500 text-white'
                                : 'bg-slate-800 text-slate-500 hover:bg-slate-700'
                            }`}
                            disabled={isLocked}
                            data-testid={`item-${item.id}-ok`}
                          >
                            <Check size={24} />
                          </button>
                          <button
                            onClick={() => !isLocked && setItemStatus(item.id, 'defect')}
                            className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              status === 'defect'
                                ? 'bg-red-500 text-white'
                                : 'bg-slate-800 text-slate-500 hover:bg-slate-700'
                            }`}
                            disabled={isLocked}
                            data-testid={`item-${item.id}-defect`}
                          >
                            <X size={24} />
                          </button>
                          <button
                            onClick={() => !isLocked && setItemStatus(item.id, 'na')}
                            className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${
                              status === 'na'
                                ? 'bg-slate-600 text-white'
                                : 'bg-slate-800 text-slate-500 hover:bg-slate-700'
                            }`}
                            disabled={isLocked}
                            data-testid={`item-${item.id}-na`}
                          >
                            <Minus size={24} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Notes */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <label className="label-uppercase">Notes / Remarques</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ajoutez des remarques si necessaire..."
            className="mt-2 min-h-[100px] bg-slate-950 border-slate-800 text-white"
            disabled={isLocked}
            data-testid="notes-input"
          />
        </div>

        {/* Signature Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="label-uppercase">Signature du chauffeur *</label>
            {!isLocked && signature && (
              <Button
                onClick={clearSignature}
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 size={16} className="mr-1" />
                Effacer
              </Button>
            )}
          </div>

          {isLocked && signature ? (
            <div className="bg-white rounded-lg p-2">
              <img
                src={signature}
                alt="Signature"
                className="w-full h-32 object-contain"
              />
            </div>
          ) : isLocked && !signature ? (
            <div className="bg-slate-800 rounded-lg p-4 text-center text-slate-400">
              Aucune signature enregistree
            </div>
          ) : (
            <div className="relative">
              <canvas
                ref={canvasRef}
                width={350}
                height={150}
                className="w-full bg-white rounded-lg border-2 border-dashed border-slate-600 touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="signature-canvas"
              />
              {!signature && (
                <p className="absolute inset-0 flex items-center justify-center text-slate-400 pointer-events-none">
                  Signez ici
                </p>
              )}
            </div>
          )}
        </div>

        {/* Warning if defects */}
        {hasDefects() && !isLocked && (
          <div
            className="bg-red-900/30 border border-red-800 rounded-xl p-4 flex items-start gap-3"
            data-testid="defect-warning"
          >
            <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={24} />
            <div>
              <p className="text-red-400 font-bold">Defauts detectes</p>
              <p className="text-red-300 text-sm mt-1">
                Des defauts ont ete identifies. Contactez votre gestionnaire avant de prendre la route.
              </p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        {!isLocked && (
          <Button
            onClick={handleSubmit}
            className="w-full btn-large btn-primary-glow"
            disabled={!isComplete() || submitting}
            data-testid="submit-inspection"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Enregistrement...
              </span>
            ) : (
              <>
                <Save size={20} className="mr-2" />
                {isReopened ? 'Confirmer la mise a jour et re-signer' : "Enregistrer l'inspection"}
              </>
            )}
          </Button>
        )}

        {isLocked && (
          <>
            <div className="text-center text-slate-400 py-4" data-testid="inspection-complete">
              <Check className="mx-auto mb-2 text-green-500" size={32} />
              <p className="font-semibold">Inspection completee</p>
              <p className="text-sm">
                Enregistree a {new Date(existingInspection.created_at).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>

            {/* Share Button */}
            <Button
              onClick={() => setShowShareDialog(true)}
              className="w-full h-16 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg font-bold"
              data-testid="share-inspection-btn"
            >
              <Share2 size={24} className="mr-3" />
              PARTAGER LE RAPPORT
            </Button>
          </>
        )}
      </div>
      </div>
      {/* End of page-wrapper */}

      {/* Share Dialog - Outside page-wrapper */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md print:hidden">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Share2 className="text-amber-500" size={24} />
              Partager l'inspection
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <p className="text-amber-500 font-bold text-lg">Inspection {existingInspection?.date}</p>
              <p className="text-slate-400 text-sm">
                {existingInspection?.overall_status === 'passed' ? '✅ Conforme' : '❌ Non-conforme'}
              </p>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Envoyer par email</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="email@exemple.com"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                />
                <Button
                  onClick={handleShareEmail}
                  disabled={!shareEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Mail size={20} />
                </Button>
              </div>
            </div>

            {/* SMS */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Envoyer par texto</label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={sharePhone}
                  onChange={(e) => setSharePhone(e.target.value)}
                  placeholder="+1 514 555 1234"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                />
                <Button
                  onClick={handleShareSMS}
                  disabled={!sharePhone}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <MessageSquare size={20} />
                </Button>
              </div>
            </div>

            {/* Print */}
            <Button
              onClick={handlePrint}
              className="w-full h-14 bg-slate-700 hover:bg-slate-600"
              data-testid="print-inspection-btn"
            >
              <Printer size={20} className="mr-2" />
              Imprimer le rapport
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print-only section - Compact inspection report (target: ~2-3 pages) */}
      <div className="inspection-print-section" style={{ display: 'none' }}>
        <div style={{ padding: '0.3in', fontFamily: 'Arial, sans-serif', fontSize: '9pt' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '8px', marginBottom: '10px' }}>
            <h1 style={{ margin: 0, fontSize: '14pt' }}>RAPPORT D'INSPECTION PRÉ-DÉPART</h1>
            <p style={{ margin: '3px 0 0 0', fontSize: '8pt', color: '#666' }}>
              Conforme au Règlement sur les normes de sécurité des véhicules routiers (SAAQ)
            </p>
          </div>
          
          {/* Driver & Vehicle Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px', padding: '8px', border: '1px solid #ccc' }}>
            <div>
              <p style={{ margin: 0, fontSize: '8pt', color: '#666' }}>CHAUFFEUR</p>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{user?.first_name} {user?.last_name}</p>
              <p style={{ margin: 0, fontSize: '8pt' }}>Permis: {user?.license_number || '—'}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '8pt', color: '#666' }}>VÉHICULE</p>
              {driverVehicle ? (
                <>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>Tracteur #{driverVehicle.unit_number} {driverVehicle.plate_number && `(${driverVehicle.plate_number})`}</p>
                  {driverVehicle.trailers && driverVehicle.trailers.length > 0 ? (
                    driverVehicle.trailers.map((trailer, idx) => (
                      <p key={trailer.id} style={{ margin: 0, fontSize: '8pt' }}>
                        Remorque #{idx + 1}: {trailer.number} {trailer.plate && `(${trailer.plate})`}
                      </p>
                    ))
                  ) : driverVehicle.trailer_number ? (
                    <p style={{ margin: 0, fontSize: '8pt' }}>Remorque: {driverVehicle.trailer_number} {driverVehicle.trailer_plate && `(${driverVehicle.trailer_plate})`}</p>
                  ) : null}
                </>
              ) : (
                <p style={{ margin: 0 }}>Non configuré</p>
              )}
            </div>
          </div>

          {/* Info row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', padding: '5px', backgroundColor: '#f5f5f5' }}>
            <span><strong>Date:</strong> {existingInspection?.date}</span>
            <span><strong>Kilométrage:</strong> {existingInspection?.mileage} km</span>
            <span><strong>Statut:</strong> <span style={{ fontWeight: 'bold', color: existingInspection?.overall_status === 'passed' ? 'green' : 'red' }}>
              {existingInspection?.overall_status === 'passed' ? '✓ CONFORME' : '✗ NON-CONFORME'}
            </span></span>
          </div>

          {/* Compact 2-column layout for categories */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {inspectionCategories.map(category => (
              <div key={category.id} style={{ breakInside: 'avoid', marginBottom: '5px' }}>
                <div style={{ backgroundColor: '#e0e0e0', padding: '3px 6px', fontWeight: 'bold', fontSize: '8pt' }}>
                  {category.name}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8pt' }}>
                  <tbody>
                    {category.items.map(item => {
                      const itemData = inspectionItems[item.id];
                      const isOk = itemData?.status === 'ok';
                      const isDefect = itemData?.status === 'defect';
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '2px 4px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.name}
                          </td>
                          <td style={{ padding: '2px 4px', textAlign: 'right', fontWeight: 'bold', color: isDefect ? 'red' : 'inherit' }}>
                            {isOk ? '✓' : isDefect ? '✗' : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Notes if any */}
          {notes && (
            <div style={{ marginTop: '10px', breakInside: 'avoid' }}>
              <div style={{ backgroundColor: '#e0e0e0', padding: '3px 6px', fontWeight: 'bold', fontSize: '8pt' }}>
                Remarques
              </div>
              <p style={{ padding: '5px', border: '1px solid #ddd', borderTop: 'none', margin: 0, fontSize: '8pt' }}>
                {notes}
              </p>
            </div>
          )}

          {/* Signature section */}
          <div style={{ marginTop: '15px', borderTop: '2px solid #000', paddingTop: '10px', breakInside: 'avoid' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '9pt' }}>Signature du chauffeur:</p>
                {signature ? (
                  <img 
                    src={signature} 
                    alt="Signature" 
                    style={{ maxWidth: '200px', height: '60px', objectFit: 'contain', border: '1px solid #ccc' }}
                  />
                ) : (
                  <div style={{ width: '200px', height: '60px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' }} />
                )}
              </div>
              <div style={{ textAlign: 'right', fontSize: '7pt', color: '#666' }}>
                <p style={{ margin: 0 }}>Je certifie avoir effectué cette inspection</p>
                <p style={{ margin: 0 }}>de manière complète et honnête.</p>
                <p style={{ margin: '5px 0 0 0' }}>Imprimé le {new Date().toLocaleString('fr-CA')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InspectionPage;
